import React, { useState, Fragment, useEffect } from 'react';
import { Col, OverlayTrigger, Pagination, Row, Tooltip } from 'react-bootstrap';
import BootstrapTable from '@ai4/react-bootstrap-table-next';
import filterFactory from '@ai4/react-bootstrap-table2-filter';
import ToolkitProvider from '@ai4/react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit';
import paginationFactory, {
	PaginationProvider,
	PaginationListStandalone,
	SizePerPageDropdownStandalone,
	PaginationTotalStandalone,
} from '@ai4/react-bootstrap-table2-paginator';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { Confirm } from '@ai4/form-viewer';
import { TableColumn, TableSlots } from '../../types/general';
import ExternalPagination, { ExternalPaginationProps } from './ExternalPagination';

export interface Props<Row> {
	id?: string;
	className?: string;
	keyField?: string;
	columns: TableColumn[];
	rows?: Row[];
	dataLoading?: boolean;
	onSelectedRow: (row: Row, args?: { clone: boolean }) => void;
	onCloneSelectedRow: (row: Row) => void;
	onDeleteRow: (row: Row) => void;
	checkFeature?: (feature: 'edit' | 'clone' | 'delete', row: Row) => boolean;
	hideToggle?: boolean;
	hideEditColumns?: boolean;
	toggleTableKey?: string;
	showCloneColumn?: boolean;
	clickRowToSelect?: boolean;
	slots?: TableSlots<Row>;
	tableProps?: any;
	hidePagination?: boolean;
	paginationExt?: ExternalPaginationProps;
}

interface CustomToggleListArgs {
	toggleColumns: any;
	onColumnToggle: (dataField: string) => void;
	toggles: Record<string, boolean>;
}

export function Table<Row = any>(props: Props<Row>) {
	const {
		id,
		className,
		keyField = 'uniqueId',
		columns,
		rows,
		dataLoading,
		onSelectedRow,
		onCloneSelectedRow,
		onDeleteRow,
		checkFeature,
		hideToggle,
		hideEditColumns,
		showCloneColumn,
		clickRowToSelect,
		tableProps = {},
		hidePagination,
		paginationExt,
	} = props;
	const [confirm, setConfirm] = useState<Row>();
	const [enhancedColumns, setEnhancedColumns] = useState<TableColumn[]>();

	const tableStateStorageKey = id ? `tableState:${id}` : undefined;

	useEffect(() => {
		let enhancedColumns = columns;
		let toggles = columns.filter((c) => !c.hidden).map((c) => c.dataField);
		if (tableStateStorageKey) {
			const raw = localStorage.getItem(tableStateStorageKey);
			if (raw) {
				try {
					const state = JSON.parse(raw) || {};
					if (state.toggles) {
						toggles = state.toggles;
					}
					enhancedColumns = columns.map<TableColumn>((column) => ({
						...column,
						hidden: !toggles.includes(column.dataField),
					}));
				} catch (e) {}
			}
		}

		if (!hideEditColumns) {
			enhancedColumns = [
				...enhancedColumns,
				{
					dataField: 'edit',
					text: '',
					editable: false,
					align: 'center',
					headerStyle: { width: 60 },
					formatter: selectFormatter,
				},
			];
			if (showCloneColumn) {
				enhancedColumns = [
					...enhancedColumns,
					{
						dataField: 'clone',
						text: '',
						editable: false,
						align: 'center',
						headerStyle: { width: 60 },
						formatter: cloneFormatter,
					},
				];
			}
			enhancedColumns = [
				...enhancedColumns,
				{
					dataField: 'delete',
					text: '',
					editable: false,
					align: 'center',
					headerStyle: { width: 60 },
					formatter: deleteFormatter,
				},
			];
		}
		setEnhancedColumns(enhancedColumns);
	}, [hideEditColumns, showCloneColumn]);

	const selectRow = {
		mode: 'radio',
		clickToSelect: true,
		hideSelectColumn: true,
		onSelect: clickRowToSelect
			? (row: any) => {
					onSelectedRow(row);
			  }
			: undefined,
	};

	const CustomToggleList = ({ toggleColumns, onColumnToggle, toggles }: CustomToggleListArgs) => (
		<div>
			<Dropdown>
				<Dropdown.Toggle id='dropdown-custom-components'>Colonne...</Dropdown.Toggle>

				<Dropdown.Menu style={{ padding: '10px' }}>
					{columns
						.filter((column) => {
							return column.text && column.text.length > 0;
						})
						.map((column) => ({
							...column,
							toggle: toggles[column.dataField],
						}))
						.map((column, index) => (
							<Form.Check
								type='checkbox'
								key={column.dataField}
								label={column.text}
								id={column.dataField}
								checked={column.toggle}
								aria-checked={column.toggle ? 'true' : 'false'}
								onChange={() => onColumnToggle(column.dataField)}
							/>
						))}
				</Dropdown.Menu>
			</Dropdown>
		</div>
	);

	const ToggleColumnsSaver = ({
		tableStateStorageKey,
		toggles,
	}: { tableStateStorageKey: string } & CustomToggleListArgs) => {
		useEffect(() => {
			const raw = localStorage.getItem(tableStateStorageKey);
			let state: any = {};
			if (raw) {
				try {
					state = JSON.parse(raw) || {};
				} catch (e) {}
			}
			state.toggles = Object.keys(toggles).filter((k) => !!toggles[k]);
			localStorage.setItem(tableStateStorageKey, JSON.stringify(state));
		}, [JSON.stringify(toggles)]);

		return null;
	};

	const customTotal = (from: number, to: number, size: number) => (
		<span className='react-bootstrap-table-pagination-total'>
			&nbsp;Risultati da {from} a {to} di {size} totali
		</span>
	);

	const pagination = !dataLoading
		? paginationFactory({
				custom: true,
				page: 1,
				paginationSize: 5,
				totalSize: (rows || []).length,
				prePageText: 'Prec.',
				nextPageText: 'Succ.',
				showTotal: true,
				paginationTotalRenderer: customTotal,
		  })
		: undefined;

	const renderTooltip = (label: string) => (props: any) =>
		(
			<Tooltip id='button-tooltip' {...props}>
				{label}
			</Tooltip>
		);

	const selectFormatter = (cell: any, row: Row, rowIndex: number, formatExtraData: any) => {
		if (checkFeature && !checkFeature('edit', row)) return null;

		const selectMe = (rowIndex: number, row: Row) => {
			onSelectedRow(row);
		};

		return (
			<OverlayTrigger placement='top' overlay={renderTooltip('modifica')}>
				<Button variant='action'>
					<i className='bi bi-pencil-square' onClick={() => selectMe(rowIndex, row)}>
						<span className='visually-hidden'>modifica</span>{' '}
					</i>
				</Button>
			</OverlayTrigger>
		);
	};

	const cloneFormatter = (cell: any, row: Row, rowIndex: number, formatExtraData: any) => {
		if (checkFeature && !checkFeature('clone', row)) return null;

		const selectMe = (rowIndex: number, row: Row) => {
			onCloneSelectedRow(row);
		};

		return (
			<OverlayTrigger placement='top' overlay={renderTooltip('duplica')}>
				<Button variant='action'>
					<i className='bi bi-files' onClick={() => selectMe(rowIndex, row)}>
						<span className='visually-hidden'>duplica</span>{' '}
					</i>
				</Button>
			</OverlayTrigger>
		);
	};

	const deleteFormatter = (cell: any, row: Row, rowIndex: number, formatExtraData: any) => {
		if (checkFeature && !checkFeature('delete', row)) return null;

		return (
			<OverlayTrigger placement='top' overlay={renderTooltip('elimina')}>
				<Button variant='action'>
					<i className='bi bi-x-lg' onClick={() => setConfirm(row)}>
						<span className='visually-hidden'>elimina</span>{' '}
					</i>
				</Button>
			</OverlayTrigger>
		);
	};

	const contentTable = (baseProps: any) => {
		return (
			<BootstrapTable
				{...baseProps}
				{...tableProps}
				bootstrap5
				selectRow={selectRow}
				filter={filterFactory()}
				filterPosition='top'
				classes={`columns-toggle-table table-hover filterTable ${className}`}
			/>
		);
	};
	const paginatedTable =
		(baseProps: any) =>
		({ paginationProps, paginationTableProps }: any) =>
			(
				<Fragment>
					{contentTable({ ...baseProps, ...(paginationExt ? {} : paginationTableProps) })}
					<Fragment>
						<div className='row react-bootstrap-table-pagination'>
							<div className='col-md-6 col-xs-6 col-sm-6 col-lg-6'>
								<SizePerPageDropdownStandalone {...paginationProps} />
								<PaginationTotalStandalone {...paginationProps} />
							</div>
							<div className='text-right col-md-6 col-xs-6 col-sm-6 col-lg-6'>
								<PaginationListStandalone {...paginationProps} />
							</div>
						</div>
					</Fragment>
				</Fragment>
			);

	if (!enhancedColumns) return null;
	const isClientPaginated = !hidePagination && !paginationExt;
	return (
		<div>
			{confirm && (
				<Confirm
					text='Sei sicuro di voler eliminare questo elemento?'
					onSuccess={() => {
						onDeleteRow(confirm);
						setConfirm(undefined);
					}}
					onCancel={() => setConfirm(undefined)}
				/>
			)}
			<ToolkitProvider keyField={keyField} data={rows || []} columns={enhancedColumns} columnToggle>
				{(props: any) => (
					<div className='table-responsive-sm table-responsive-md' style={{ paddingBottom: '25px' }}>
						{hideToggle === true ? null : (
							<div style={{ float: 'right', marginBottom: '15px' }}>
								{tableStateStorageKey && (
									<ToggleColumnsSaver
										tableStateStorageKey={tableStateStorageKey}
										{...props.columnToggleProps}
									/>
								)}
								<CustomToggleList {...props.columnToggleProps} />
							</div>
						)}
						{dataLoading && (
							<div className='clear d-flex justify-content-center'>
								<div className='p-5'>
									<Spinner animation='border' />
								</div>
							</div>
						)}
						{!dataLoading && (
							<Fragment>
								{isClientPaginated && (
									<PaginationProvider pagination={hidePagination ? {} : pagination}>
										{paginatedTable(props.baseProps)}
									</PaginationProvider>
								)}
								{!isClientPaginated && (
									<Fragment>
										{contentTable(props.baseProps)}
										{paginationExt && <ExternalPagination {...paginationExt} />}
									</Fragment>
								)}
							</Fragment>
						)}
					</div>
				)}
			</ToolkitProvider>
		</div>
	);
}

export default Table;
